import {computed, ref, watchEffect} from 'vue'
import {useStore} from 'shared/stores/store'

export const useLabels = () => {
	const store = useStore()

	const workspaceLabels = computed(() => {
		return store.workspace.labels || []
	})

	const search = ref('')
	const searchedLabels = ref(workspaceLabels.value)
	const hasExactSearchMatch = ref(false)

	watchEffect(() => {
		if (!search.value) {
			searchedLabels.value = workspaceLabels.value
			hasExactSearchMatch.value = false
			return
		}
		const searchLowerCase = search.value.trim().toLowerCase()
		searchedLabels.value = workspaceLabels.value.filter(({label}) => label.toLowerCase().includes(searchLowerCase))
		hasExactSearchMatch.value = workspaceLabels.value.some(({label}) => label.toLowerCase() === searchLowerCase)
	})

	// SESSION LABELS
	// applying labels to sessions
	async function insertSessionsLabels(labels, sessionIds) {
		if (!Array.isArray(labels)) labels = [labels]
		if (!Array.isArray(sessionIds)) sessionIds = [sessionIds]
		const operation = (await import('shared/apollo/insertSessionsLabels')).default
		return operation({
			sessionIds,
			labels,
			collectionId: store.collection.id,
			userId: store.user.id,
		})
	}
	// removing labels from sessions
	async function deleteSessionsLabels(labels, sessionIds) {
		if (!Array.isArray(labels)) labels = [labels]
		if (!Array.isArray(sessionIds)) sessionIds = [sessionIds]
		const operation = (await import('shared/apollo/deleteSessionsLabels')).default
		return operation({
			sessionIds,
			labelIds: labels.map(({id}) => id),
			collectionId: store.collection.id,
			userId: store.user.id,
		})
	}

	// WORKSPACE LABELS
	async function insertWorkspaceLabel({labelText, workspaceId}) {
		if (!labelText || typeof labelText !== 'string' || !labelText.trim()) return
		labelText = labelText.trim()
		const {getRandomLabelColor} = await import('shared/utils/labelColors')
		const insertWorkspaceLabel = (await import('shared/apollo/insertWorkspaceLabel')).default
		const {data} = await insertWorkspaceLabel({
			label: labelText,
			workspaceId,
			color: getRandomLabelColor(),
		})
		return data?.label
	}

	const isCreatingAndApplyingLabel = ref(false)

	async function createAndApplyLabel({labelText, sessionIds, workspaceId}) {
		if (isCreatingAndApplyingLabel.value) return
		isCreatingAndApplyingLabel.value = true
		let label
		try {
			label = await insertWorkspaceLabel({labelText, workspaceId})
			await insertSessionsLabels(label, sessionIds)
		} finally {
			isCreatingAndApplyingLabel.value = false
		}
		return label
	}

	return {
		workspaceLabels,
		searchedLabels,
		search,
		hasExactSearchMatch,
		isCreatingAndApplyingLabel,
		insertSessionsLabels,
		deleteSessionsLabels,
		insertWorkspaceLabel,
		createAndApplyLabel,
	}
}
